import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, FileDiff, ListChecks, MessageSquare, Paperclip, Send } from 'lucide-react';
import { useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { api, type Attachment, type Paginated, type UserRef, type WorkflowRunStatus } from '../lib/api';
import { P, useAuth } from '../lib/auth';
import { enumLabel, useI18n, type TranslationKey } from '../lib/i18n';
import { formatDateTime, formatNumber } from '../lib/format';
import { AttachmentComposer, AttachmentList, DropZone, useUploads } from '../components/Attachments';
import { TaskHolderName } from '../components/TaskHolder';
import {
  Badge,
  Button,
  Card,
  CardHeader,
  Detail,
  EmptyState,
  ErrorState,
  Input,
  LoadingBlock,
  PageHeader,
  Pagination,
  Table,
  Tabs,
  Td,
  Textarea,
  Th,
  Tr,
  statusTone,
  useToast,
} from '../components/ui';

/**
 * Work › Client CRs — the change requests clients raise on the client portal.
 * Admins and team leaders (`client_crs.manage`) read them and move them along:
 * New → In review → Approved or Rejected → Done, with a note the client reads.
 * Both sides write to each other on one until it is done, and an approved one is
 * converted into a task — for a person or a workflow — that carries its files.
 */

export type ChangeRequestStatus = 'NEW' | 'IN_REVIEW' | 'APPROVED' | 'REJECTED' | 'DONE';
export const CR_STATUSES: ChangeRequestStatus[] = ['NEW', 'IN_REVIEW', 'APPROVED', 'REJECTED', 'DONE'];

interface ClientRef {
  id: string;
  code: string;
  companyName: string;
  companyNameAr: string | null;
}

interface ChangeRequestRow {
  id: string;
  number: string;
  title: string;
  status: ChangeRequestStatus;
  reviewNote: string | null;
  reviewedAt: string | null;
  createdAt: string;
  updatedAt: string;
  client: ClientRef;
  project: { id: string; code: string; name: string; nameAr: string | null } | null;
  reviewedBy: { id: string; fullName: string; fullNameAr: string | null } | null;
  /** The request's own files, its messages, and the tasks it became (called-off ones left out). */
  _count: { attachments: number; comments: number; tasks: number };
}

/**
 * One of the client's portal sign-ins: the company's own (no contact), or one of
 * its contacts' (2026-10-11).
 */
interface ClientSignIn {
  id: string;
  username: string;
  contact: { name: string; jobTitle: string | null } | null;
}

/** A message on a change request: the client's, or one of ours. */
export interface ChangeRequestMessage {
  id: string;
  body: string;
  createdAt: string;
  fromClient: boolean;
  author: (UserRef & { jobTitle: string | null }) | null;
  /** Which of the client's sign-ins wrote it; an API from before sends none. */
  clientAuthor?: ClientSignIn | null;
  attachments: Attachment[];
}

/** A task the request was converted into. */
export interface ChangeRequestTask {
  id: string;
  title: string;
  status: string;
  startAt: string | null;
  dueAt: string | null;
  completedAt: string | null;
  createdAt: string;
  workflowLevel: number | null;
  assignee: UserRef | null;
  createdBy: UserRef;
  workflow: {
    runId: string;
    status: WorkflowRunStatus;
    currentLevel: number;
    levelCount: number | null;
    /** A saved workflow's name, for work started from one before workflows were built on the task (2026-10-15). */
    name: string | null;
    nameAr: string | null;
  } | null;
}

export interface ChangeRequestDetail extends ChangeRequestRow {
  details: string | null;
  /** The sign-in that raised it; a contact's names them. */
  account: Omit<ClientSignIn, 'contact'> & { contact?: ClientSignIn['contact'] };
  attachments: Attachment[];
  events: Array<{
    id: string;
    fromStatus: ChangeRequestStatus | null;
    toStatus: ChangeRequestStatus;
    note: string | null;
    occurredAt: string;
    actor: { id: string; fullName: string; fullNameAr: string | null } | null;
  }>;
  comments: ChangeRequestMessage[];
  tasks: ChangeRequestTask[];
  nextStatuses: ChangeRequestStatus[];
  /** Both sides write on it until it is done. */
  canReply: boolean;
  /** Approved, and not converted yet — or the work it became was called off. */
  canConvert: boolean;
  /** Its work is all finished: the cue to mark it done. */
  workFinished: boolean;
}

/** A change request's status in the reader's language — "New" is masculine in Arabic here. */
export function crStatusLabel(t: (key: TranslationKey) => string, status: ChangeRequestStatus): string {
  return t(`crs.status.${status}` as TranslationKey);
}

export function clientName(client: Pick<ClientRef, 'companyName' | 'companyNameAr'> | null | undefined, locale: string): string {
  if (!client) return '—';
  return locale === 'ar' && client.companyNameAr ? client.companyNameAr : client.companyName;
}

export function ClientCrsPage() {
  const { t, locale, localized } = useI18n();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const status = CR_STATUSES.includes(params.get('status') as ChangeRequestStatus)
    ? (params.get('status') as ChangeRequestStatus)
    : '';
  const [page, setPage] = useState(1);
  const [q, setQ] = useState('');

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['client-crs', status, page, q],
    queryFn: () =>
      api.get<Paginated<ChangeRequestRow> & { counts: Record<ChangeRequestStatus, number> }>('/client-change-requests', {
        status: status || undefined,
        q: q.trim() || undefined,
        page,
        pageSize: 25,
      }),
  });

  const counts = data?.counts;
  const all = counts ? CR_STATUSES.reduce((sum, s) => sum + (counts[s] ?? 0), 0) : undefined;

  return (
    <>
      <PageHeader title={t('crs.title')} subtitle={t('crs.subtitle')} count={data?.total} />

      <Tabs
        active={status || 'all'}
        onChange={(id) => {
          setPage(1);
          setParams(id === 'all' ? {} : { status: id }, { replace: true });
        }}
        tabs={[
          { id: 'all', label: t('common.all'), count: all },
          ...CR_STATUSES.map((s) => ({ id: s, label: crStatusLabel(t, s), count: counts?.[s] })),
        ]}
      />

      <Card className="mt-4" padded={false}>
        <div className="p-4">
          <Input
            placeholder={t('crs.searchPlaceholder')}
            aria-label={t('common.search')}
            value={q}
            onChange={(e) => {
              setQ(e.target.value);
              setPage(1);
            }}
            className="max-w-sm"
          />
        </div>
        {isLoading ? (
          <LoadingBlock />
        ) : error ? (
          <ErrorState error={error} onRetry={() => void refetch()} />
        ) : !data?.items.length ? (
          <EmptyState icon={<FileDiff className="h-8 w-8" />} title={t('crs.empty')} hint={t('crs.emptyHint')} />
        ) : (
          <>
            <Table>
              <thead>
                <tr>
                  <Th>{t('crs.number')}</Th>
                  <Th>{t('crs.titleField')}</Th>
                  <Th>{t('crs.client')}</Th>
                  <Th>{t('common.status')}</Th>
                  <Th align="end">{t('crs.messages')}</Th>
                  <Th align="end">{t('crs.files')}</Th>
                  <Th align="end">{t('common.createdAt')}</Th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((request) => (
                  <Tr key={request.id} onClick={() => navigate(`/client-crs/${request.id}`)}>
                    <Td>
                      <span className="whitespace-nowrap font-mono text-mono-sm text-primary-600 dark:text-primary-400">{request.number}</span>
                    </Td>
                    <Td className="max-w-md">
                      <span className="block truncate">{request.title}</span>
                      <span className="block truncate text-caption text-faint">
                        {request.project ? localized(request.project) : t('common.general')}
                      </span>
                    </Td>
                    <Td className="whitespace-nowrap text-muted">{clientName(request.client, locale)}</Td>
                    <Td>
                      <span className="flex flex-wrap items-center gap-1.5">
                        <Badge tone={statusTone(request.status)}>{crStatusLabel(t, request.status)}</Badge>
                        {request._count.tasks > 0 && (
                          <Badge tone="plasma">
                            <ListChecks className="h-3 w-3" aria-hidden />
                            {t('crs.convertedBadge')}
                          </Badge>
                        )}
                      </span>
                    </Td>
                    <Td align="end" className="text-muted">
                      <span className="inline-flex items-center gap-1 tabular">
                        <MessageSquare className="h-3.5 w-3.5" aria-hidden />
                        {request._count.comments}
                      </span>
                    </Td>
                    <Td align="end" className="text-muted">
                      <span className="inline-flex items-center gap-1 tabular">
                        <Paperclip className="h-3.5 w-3.5" aria-hidden />
                        {request._count.attachments}
                      </span>
                    </Td>
                    <Td align="end" className="whitespace-nowrap text-caption text-muted">
                      {formatDateTime(request.createdAt, locale)}
                    </Td>
                  </Tr>
                ))}
              </tbody>
            </Table>
            <Pagination page={data.page} pageCount={data.pageCount} total={data.total} onChange={setPage} />
          </>
        )}
      </Card>
    </>
  );
}

export function ClientCrDetailPage() {
  const { id = '' } = useParams();
  const { t, locale, localized } = useI18n();
  const { can } = useAuth();
  const navigate = useNavigate();

  const { data: request, isLoading, error, refetch } = useQuery({
    queryKey: ['client-cr', id],
    queryFn: () => api.get<ChangeRequestDetail>(`/client-change-requests/${id}`),
    // The client's answers show without a reload.
    refetchInterval: 60_000,
  });

  if (isLoading) return <LoadingBlock />;
  if (error || !request) return <ErrorState error={error} onRetry={() => void refetch()} />;

  const manages = can(P.CLIENT_CRS_MANAGE);
  const mayConvert = manages && can(P.TASKS_CREATE) && request.canConvert;

  return (
    <>
      <div className="mb-4">
        <Link to="/client-crs" className="inline-flex items-center gap-1.5 text-body-sm text-muted hover:text-[var(--text-primary)]">
          <ArrowLeft className="h-4 w-4 flip-rtl" aria-hidden />
          {t('nav.clientCrs')}
        </Link>
      </div>

      <PageHeader
        title={
          <span className="flex flex-wrap items-center gap-3">
            <span className="font-mono text-h2 text-primary-600 dark:text-primary-400">{request.number}</span>
            <span>{request.title}</span>
          </span>
        }
        subtitle={
          <span className="flex flex-wrap items-center gap-2">
            <Badge tone={statusTone(request.status)}>{crStatusLabel(t, request.status)}</Badge>
            <span className="text-muted">
              {clientName(request.client, locale)}
              {request.project ? ` · ${localized(request.project)}` : ''}
            </span>
          </span>
        }
        action={
          manages &&
          (request.nextStatuses.length > 0 || mayConvert) && (
            <div className="flex flex-wrap gap-2">
              {mayConvert && (
                <Button
                  variant="primary"
                  icon={<ListChecks className="h-4 w-4" />}
                  onClick={() => navigate(`/client-crs/${id}/convert`)}
                >
                  {t('crs.convert')}
                </Button>
              )}
              {request.nextStatuses.map((next) => (
                <Button
                  key={next}
                  variant={
                    next === 'REJECTED'
                      ? 'danger'
                      : // Converting comes first; Done waits for the work.
                        (next === 'APPROVED' || next === 'DONE') && !mayConvert
                        ? 'primary'
                        : 'secondary'
                  }
                  onClick={() => navigate(`/client-crs/${id}/status?to=${next}`)}
                >
                  {t(`crs.action.${next}` as TranslationKey)}
                </Button>
              ))}
            </div>
          )
        }
      />

      <div className="grid gap-5 xl:grid-cols-3">
        <div className="min-w-0 space-y-5 xl:col-span-2">
          {request.reviewNote && (
            <Card className="border-primary/30">
              <p className="mb-1 text-caption uppercase text-[var(--text-tertiary)]">{t('crs.lastNote')}</p>
              <p className="whitespace-pre-wrap text-body-sm text-[var(--text-primary)]" dir="auto">
                {request.reviewNote}
              </p>
            </Card>
          )}

          <Card>
            <CardHeader title={t('crs.details')} />
            {request.details ? (
              <p className="whitespace-pre-wrap text-body-sm text-[var(--text-primary)]" dir="auto">
                {request.details}
              </p>
            ) : (
              <p className="text-body-sm text-muted">—</p>
            )}
            {request.attachments.length > 0 && (
              <div className="mt-4 border-t hairline pt-4">
                <p className="mb-2 text-caption uppercase text-[var(--text-tertiary)]">{t('crs.files')}</p>
                <AttachmentList attachments={request.attachments} />
              </div>
            )}
          </Card>

          <Conversation request={request} canWrite={manages} />
        </div>

        <div className="min-w-0 space-y-5">
          <Card>
            <dl className="divide-y divide-[var(--line)]">
              <Detail label={t('crs.client')}>
                <Link to={`/clients/${request.client.id}`} className="text-primary-600 dark:text-primary-400 hover:underline">
                  {clientName(request.client, locale)}
                </Link>
              </Detail>
              <Detail label={t('crs.raisedByLabel')}>
                {/* One of its contacts with their own sign-in, or the company's own (2026-10-11). */}
                <span className="inline-flex flex-col items-end">
                  <span>{request.account.contact?.name ?? clientName(request.client, locale)}</span>
                  <bdi dir="ltr" className="text-caption text-muted">
                    {request.account.username}
                  </bdi>
                </span>
              </Detail>
              <Detail label={t('common.project')}>
                {request.project ? (
                  <Link to={`/projects/${request.project.id}`} className="text-primary-600 dark:text-primary-400 hover:underline">
                    {localized(request.project)}
                  </Link>
                ) : (
                  t('common.general')
                )}
              </Detail>
              <Detail label={t('common.createdAt')}>{formatDateTime(request.createdAt, locale)}</Detail>
              {request.reviewedBy && (
                <Detail label={t('crs.reviewedBy')}>
                  {localized(request.reviewedBy)}
                  {request.reviewedAt && (
                    <span className="block text-caption text-faint">{formatDateTime(request.reviewedAt, locale)}</span>
                  )}
                </Detail>
              )}
            </dl>
          </Card>

          {(request.tasks.length > 0 || request.status === 'APPROVED') && (
            <TasksCard request={request} mayConvert={mayConvert} />
          )}

          <Card>
            <CardHeader title={t('crs.history')} />
            <ol className="relative">
              {request.events.map((event, index) => (
                <li key={event.id} className="relative flex gap-3 pb-4 last:pb-0">
                  <span className="relative flex flex-col items-center">
                    <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-primary" aria-hidden />
                    {index < request.events.length - 1 && <span className="w-px flex-1 bg-[var(--line)]" aria-hidden />}
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <Badge tone={statusTone(event.toStatus)}>{crStatusLabel(t, event.toStatus)}</Badge>
                      <span className="text-caption text-muted">
                        {event.actor
                          ? localized(event.actor)
                          : request.account.contact
                            ? t('crs.raisedByContact', { name: request.account.contact.name })
                            : t('crs.raisedBy')}
                      </span>
                    </div>
                    <p className="mt-0.5 text-caption text-faint tabular">{formatDateTime(event.occurredAt, locale)}</p>
                    {event.note && (
                      <p className="mt-1 whitespace-pre-wrap text-body-sm text-muted" dir="auto">
                        {event.note}
                      </p>
                    )}
                  </div>
                </li>
              ))}
            </ol>
          </Card>
        </div>
      </div>
    </>
  );
}

/**
 * The messages between the client and us — words, files, voice notes. The
 * client reads every one on the portal; Done closes the conversation.
 */
function Conversation({ request, canWrite }: { request: ChangeRequestDetail; canWrite: boolean }) {
  const { t, locale, localized } = useI18n();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [body, setBody] = useState('');
  const uploads = useUploads();
  const canPost = (body.trim() !== '' || uploads.fileIds.length > 0) && !uploads.busy;

  const post = useMutation({
    mutationFn: () =>
      api.post(`/client-change-requests/${request.id}/replies`, {
        body: body.trim() || undefined,
        fileIds: uploads.fileIds.length ? uploads.fileIds : undefined,
      }),
    onSuccess: () => {
      setBody('');
      uploads.clear();
      toast.success(t('crs.messageSent'));
      void queryClient.invalidateQueries({ queryKey: ['client-cr', request.id] });
      void queryClient.invalidateQueries({ queryKey: ['client-crs'] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <Card>
      <CardHeader
        title={t('crs.conversation')}
        action={<Badge tone="neutral" className="tabular">{request.comments.length}</Badge>}
      />

      {request.comments.length ? (
        <ul className="mb-4 space-y-3">
          {request.comments.map((message) => (
            <li
              key={message.id}
              className={
                'rounded-md border p-3 ' +
                (message.fromClient ? 'border-ai-plasma/25 bg-ai-plasma/6 me-6' : 'border-[var(--line)] ms-6')
              }
            >
              <div className="mb-1 flex flex-wrap items-baseline gap-2">
                <span className="text-body-sm font-medium text-[var(--text-primary)]">
                  {message.fromClient
                    ? message.clientAuthor?.contact
                      ? `${message.clientAuthor.contact.name} · ${clientName(request.client, locale)}`
                      : clientName(request.client, locale)
                    : localized(message.author)}
                </span>
                {message.fromClient ? (
                  <Badge tone="plasma">{t('tickets.clientBadge')}</Badge>
                ) : (
                  message.author?.jobTitle && <span className="text-caption text-muted">{message.author.jobTitle}</span>
                )}
                <span className="text-caption text-faint">{formatDateTime(message.createdAt, locale)}</span>
              </div>
              {message.body && (
                <p className="whitespace-pre-wrap text-body-sm text-[var(--text-primary)]" dir="auto">
                  {message.body}
                </p>
              )}
              <AttachmentList attachments={message.attachments} className={message.body ? 'mt-2' : undefined} />
            </li>
          ))}
        </ul>
      ) : (
        <p className="mb-4 text-body-sm text-muted">{t('crs.noMessages')}</p>
      )}

      {!request.canReply ? (
        <p className="rounded-md border hairline bg-[var(--surface-sunken)] px-3 py-2 text-body-sm text-muted">
          {t('crs.conversationClosed')}
        </p>
      ) : (
        canWrite && (
          <>
            <p className="mb-2 text-caption text-muted">{t('crs.clientReadsMessages')}</p>
            <DropZone uploads={uploads}>
              <Textarea
                value={body}
                onChange={(e) => setBody(e.target.value)}
                onPaste={uploads.onPaste}
                placeholder={t('crs.messagePlaceholder')}
                aria-label={t('crs.messagePlaceholder')}
                className="min-h-[80px]"
              />
              <AttachmentComposer
                uploads={uploads}
                className="mt-2"
                actions={
                  <Button
                    variant="primary"
                    size="sm"
                    icon={<Send className="h-4 w-4 flip-rtl" />}
                    disabled={!canPost}
                    loading={post.isPending}
                    onClick={() => post.mutate()}
                  >
                    {t('crs.send')}
                  </Button>
                }
              />
            </DropZone>
          </>
        )
      )}
    </Card>
  );
}

/**
 * The tasks the request was converted into, and how far they have got. The
 * request does not become Done by itself (the operator's choice): when the work
 * is finished, this says so, and a reviewer marks it done.
 */
function TasksCard({ request, mayConvert }: { request: ChangeRequestDetail; mayConvert: boolean }) {
  const { t, locale, localized } = useI18n();
  const navigate = useNavigate();
  const live = request.tasks.filter((task) => task.status !== 'CANCELLED');
  const done = live.filter((task) => task.status === 'DONE').length;
  const first = request.tasks[0];
  const n = (value: number) => formatNumber(value, locale);

  return (
    <Card>
      <CardHeader
        title={t('crs.tasks')}
        subtitle={live.length > 0 ? t('crs.tasksProgress', { done: n(done), total: n(live.length) }) : undefined}
        action={
          mayConvert && (
            <Button size="sm" icon={<ListChecks className="h-4 w-4" />} onClick={() => navigate(`/client-crs/${request.id}/convert`)}>
              {t('crs.convert')}
            </Button>
          )
        }
      />

      {request.tasks.length === 0 ? (
        <p className="text-body-sm text-muted">
          {t('crs.notConverted')} {mayConvert && t('crs.convertHint')}
        </p>
      ) : (
        <ul className="space-y-3">
          {request.tasks.map((task) => (
            <li key={task.id} className="rounded-md border hairline p-3">
              <div className="flex flex-wrap items-start justify-between gap-2">
                <Link to={`/tasks/${task.id}`} className="min-w-0 break-words text-body-sm font-medium text-primary-600 dark:text-primary-400 hover:underline" dir="auto">
                  {task.title}
                </Link>
                <Badge tone={statusTone(task.status)}>{enumLabel(task.status, locale)}</Badge>
              </div>
              <p className="mt-1 text-caption text-muted">
                <TaskHolderName task={task} empty={t('common.unassigned')} />
                {task.workflow && (
                  <>
                    {' · '}
                    {task.workflow.name
                      ? localized({ name: task.workflow.name, nameAr: task.workflow.nameAr })
                      : t('tasks.workflow')}{' '}
                    —{' '}
                    {t('crs.levelOf', {
                      n: n(task.workflowLevel ?? 1),
                      total: n(task.workflow.levelCount ?? task.workflowLevel ?? 1),
                    })}
                  </>
                )}
              </p>
              <p className="mt-0.5 text-caption text-faint tabular">
                {task.startAt ? formatDateTime(task.startAt, locale) : '—'} {locale === 'ar' ? '←' : '→'}{' '}
                {task.dueAt ? formatDateTime(task.dueAt, locale) : t('crs.noDeadline')}
              </p>
            </li>
          ))}
        </ul>
      )}

      {first && (
        <p className="mt-3 text-caption text-faint">
          {t('crs.convertedBy', { name: localized(first.createdBy), when: formatDateTime(first.createdAt, locale) })}
        </p>
      )}
      {request.status === 'APPROVED' && request.workFinished && (
        <p className="mt-3 rounded-md border border-success/30 bg-success/8 px-3 py-2 text-body-sm text-success-fg dark:text-success">
          {t('crs.workFinished')}
        </p>
      )}
      {request.status === 'APPROVED' && request.canConvert && request.tasks.length > 0 && (
        <p className="mt-3 rounded-md border border-warning/35 bg-warning/8 px-3 py-2 text-body-sm text-warning-fg dark:text-warning-dark">
          {t('crs.workCalledOff')}
        </p>
      )}
    </Card>
  );
}
